// Reservation operations workspace for multi-facility staff support.
//
// The server owns date/status/search filtering, tenant isolation, ordering, and
// pagination. Existing Facility mutation routes remain the only cancellation,
// no-show, and court-block write paths.
//
// Sources:
//   GET    /facilities/{id}/reservation-operations    -> paginated read model
//   GET    /facilities/{id}/courts                     -> block-court form
//   POST   /facilities/bookings/{id}/cancellation-quotes -> authoritative decision
//   GET    /permissions/check?facility_id=&action=     -> RBAC gate (server-computed)
//   POST   /facilities/bookings/{id}/cancel            -> cancel (confirmed-only, RBAC)
//   POST   /facilities/bookings/{id}/no-show           -> no-show (confirmed-only, RBAC)
//
// The cancel is a two-step CONFIRM flow (Task #71 Slice B): clicking "Cancel
// Booking" transitions the detail dialog to a confirm sub-step that loads the
// server-issued decision, optionally collects a reason, and — for an operator holding
// facility:refund_override (server-computed, never derived client-side) — exposes
// an adjustable refund bounded to [0, amount_charged]. A deviation from the policy
// refund requires a reason (mirrors the backend guardrail). Local MUI feedback
// surfaces the ACTUAL server refund, not the quoted expectation.

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { endOfWeek, format, startOfWeek, subDays, subWeeks } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { parseDecimal, formatMoney } from "@bw/shared/utils";
import { asMoney, type Money } from "@bw/shared/types";
import {
  isApiError,
  isBookingCancellationQuoteExpired,
  isValidBookingCancellationQuote,
  MAX_RESERVATION_OPERATIONS_OFFSET,
  requiresExactCancellationRetry,
} from "@bw/shared/api";
import { useReservationStateStream } from "@bw/shared/hooks";

import { useResolvedFacility } from "@/hooks/use-selected-facility";

import { ReservationOperationsTable } from "@/components/bookings/reservation-operations-table";
import { listCourts, type Court } from "@/libs/facilityApi";
import {
  cancelBooking,
  recoverBookingCancellation,
  createBookingCancellationQuote,
  markNoShow,
  searchReservationOperations,
  type ReservationOperation as Booking,
  type ReservationOperationSort,
  type BookingCancelBody,
  type BookingCancellationQuote,
} from "@/libs/bookingsApi";
import { hasStaffScheduleRole } from "@/libs/facilityRoles";
import { checkFacilityPermission } from "@/libs/permissionsApi";
import {
  createZonedTimeFormatter,
  toZonedCivilDate,
} from "@/libs/zonedDateTime";
import { BlockCourtDialog } from "@/components/bookings/block-court-dialog";
import { CreateBookingDialog } from "@/components/bookings/create-booking-dialog";

// A staff court block / facility event (#1674) is a $0 booking with NO player —
// it carries an ``event_name`` instead. Render it as a distinct, non-$ row.
const isCourtBlock = (b: Booking): boolean => Boolean(b.event_name);

// The refund-adjust control is gated on this server-computed action.
const REFUND_OVERRIDE_ACTION = "facility:refund_override";
// The no-show action is gated on this server-computed action (same facility-
// schedule grant the rest of the day-of-bookings operator surface uses). The FE
// ASKS the server (checkFacilityPermission) — it never derives the answer locally.
const NO_SHOW_ACTION = "facility:schedule";
const DEFAULT_FACILITY_TIMEZONE = "America/Los_Angeles";

interface BookingDateTimeFormatters {
  date: Intl.DateTimeFormat;
  time: Intl.DateTimeFormat;
}

const formatBookingTime = (
  instant: string,
  formatters: BookingDateTimeFormatters,
): string => formatters.time.format(new Date(instant));

const formatBookingDateTime = (
  instant: string,
  formatters: BookingDateTimeFormatters,
): string =>
  `${formatters.date.format(new Date(instant))} · ${formatBookingTime(
    instant,
    formatters,
  )}`;

// The dialog is a tiny state machine: the row click opens "detail"; clicking
// "Cancel Booking" advances to "confirm" (which loads a quote + collects input);
// clicking "Mark No-Show" advances to "noshow_confirm" (an honest no-refund
// confirm — NO preview); any Back/dismiss returns to "detail" without acting.
type DialogStep = "detail" | "confirm" | "noshow_confirm";

interface FeedbackMessage {
  severity: "success" | "error";
  message: string;
}

// A cancellation is fail-closed until the complete server quote is ready. The
// client never reconstructs fee/refund values from the selected booking.
type CancellationQuoteState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; quote: BookingCancellationQuote }
  | { status: "error" };

// The status filter pills, in display order. Labels are the exact button names
// the page exposes; "all" is the no-op default.
const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "confirmed", label: "Confirmed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
  { value: "no_show", label: "No-Show" },
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number]["value"];
type BookingStatusFilter = Exclude<StatusFilter, "all">;

const PAGE_SIZE = 50;
const MAX_PAGE = Math.floor(MAX_RESERVATION_OPERATIONS_OFFSET / PAGE_SIZE) + 1;
const MAX_REACHABLE_RESULTS = MAX_RESERVATION_OPERATIONS_OFFSET + PAGE_SIZE;
const BOOKING_STATUSES = new Set<BookingStatusFilter>([
  "confirmed",
  "cancelled",
  "completed",
  "no_show",
]);
const RESERVATION_SORTS = new Set<ReservationOperationSort>([
  "start_time",
  "created_at",
  "status",
  "facility",
  "court",
  "player",
]);

function validIsoDate(value: string | null, fallback: string): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return fallback;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
    ? fallback
    : value;
}

const humanizeStatus = (status: string): string => {
  if (!status) return "—";
  // "no_show" -> "No-show"; the underscore reads as a hyphen so a multi-word
  // status never renders a raw enum token.
  const label = status.replace(/_/g, "-");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

function bookingStatusColor(
  status: string,
): "default" | "error" | "info" | "success" | "warning" {
  if (status === "confirmed") return "success";
  if (status === "completed") return "info";
  if (status === "cancelled") return "error";
  if (status === "no_show") return "warning";
  return "default";
}

export function BookingsWorkspace() {
  // The active facility comes from the FacilityContext selection, resolved
  // against the org's facility list (cross-org guard #883). The Layer-3 gate owns
  // the genuine "no facility" case — this workspace only handles the brief
  // resolve window and gates every read on a truthy facilityId.
  const {
    facility: selectedFacility,
    facilityId: selectedFacilityId,
    facilities: authorizedFacilities,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const reservationFacilities = useMemo(
    () =>
      authorizedFacilities.filter((candidate) =>
        candidate.allowed_actions.some(
          (action) =>
            action === "facility:manage" || action === "facility:schedule",
        ),
      ),
    [authorizedFacilities],
  );
  const facility =
    reservationFacilities.find(
      (candidate) => candidate.facility_id === selectedFacilityId,
    ) ??
    reservationFacilities.find((candidate) =>
      candidate.allowed_actions.includes("facility:schedule"),
    ) ??
    reservationFacilities[0] ??
    null;
  const facilityId = facility?.facility_id ?? null;
  const scheduleFacilityIds = useMemo(
    () =>
      new Set(
        reservationFacilities
          .filter((candidate) =>
            candidate.allowed_actions.includes("facility:schedule"),
          )
          .map((candidate) => candidate.facility_id),
      ),
    [reservationFacilities],
  );
  const canScheduleAnchor = Boolean(
    facilityId && scheduleFacilityIds.has(facilityId),
  );
  const facilityTimeZone = facility?.timezone || DEFAULT_FACILITY_TIMEZONE;
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityOptions = useMemo(
    () =>
      reservationFacilities.length > 0
        ? reservationFacilities
        : facility
          ? [facility]
          : [],
    [reservationFacilities, facility],
  );
  const facilityToday = toZonedCivilDate(new Date(), facilityTimeZone);
  const today = format(facilityToday, "yyyy-MM-dd");
  const fromDate = validIsoDate(searchParams.get("from"), today);
  const requestedToDate = validIsoDate(searchParams.get("to"), fromDate);
  const toDate = requestedToDate < fromDate ? fromDate : requestedToDate;
  const availableFacilityIds = useMemo(
    () => new Set(facilityOptions.map((item) => item.facility_id)),
    [facilityOptions],
  );
  const searchParamsKey = searchParams.toString();
  const selectedFacilityIds = useMemo(
    () =>
      new URLSearchParams(searchParamsKey)
        .getAll("facility")
        .filter((id) => availableFacilityIds.has(id)),
    [availableFacilityIds, searchParamsKey],
  );
  const selectedStatuses = useMemo(
    () =>
      new URLSearchParams(searchParamsKey)
        .getAll("status")
        .filter((value): value is BookingStatusFilter =>
          BOOKING_STATUSES.has(value as BookingStatusFilter),
        ),
    [searchParamsKey],
  );
  const committedSearch = searchParams.get("q")?.trim() ?? "";
  const requestedSort = searchParams.get(
    "sort",
  ) as ReservationOperationSort | null;
  const sortBy =
    requestedSort && RESERVATION_SORTS.has(requestedSort)
      ? requestedSort
      : "start_time";
  const sortDirection =
    searchParams.get("direction") === "desc" ? "desc" : "asc";
  const requestedPageParam = searchParams.get("page");
  const parsedPage = Number(requestedPageParam ?? "1");
  const normalizedPage =
    Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const page = Math.min(normalizedPage, MAX_PAGE);
  const pageNeedsCanonicalization =
    requestedPageParam !== null && requestedPageParam !== String(page);
  const [searchDraft, setSearchDraft] = useState(committedSearch);

  useEffect(() => setSearchDraft(committedSearch), [committedSearch]);

  const updateSearchParams = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (!pageNeedsCanonicalization) return;
    updateSearchParams((next) => {
      if (page === 1) next.delete("page");
      else next.set("page", String(page));
    });
  }, [page, pageNeedsCanonicalization, updateSearchParams]);

  const setRepeatedFilter = useCallback(
    (key: string, values: string[]) => {
      updateSearchParams((next) => {
        next.delete(key);
        values.forEach((value) => next.append(key, value));
        next.delete("page");
      });
    },
    [updateSearchParams],
  );

  // The "Block court / create event" form dialog (#1674).
  const [blockOpen, setBlockOpen] = useState(false);
  const blockTriggerRef = useRef<HTMLButtonElement>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const createTriggerRef = useRef<HTMLButtonElement>(null);
  const [courts, setCourts] = useState<Court[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackMessage | null>(null);
  const clearErrorFeedback = useCallback(
    () =>
      setFeedback((current) =>
        current?.severity === "error" ? null : current,
      ),
    [],
  );
  const [facilityMenuAnchor, setFacilityMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<Booking | null>(null);
  const detailTimeZone = selected?.facility_timezone || facilityTimeZone;
  const bookingDateTimeFormatters = useMemo<BookingDateTimeFormatters>(
    () => ({
      date: new Intl.DateTimeFormat("en-US", {
        timeZone: detailTimeZone,
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      time: createZonedTimeFormatter(detailTimeZone),
    }),
    [detailTimeZone],
  );
  // Async permission and quote callbacks verify both the selected booking and a
  // monotonic request generation before publishing their result.
  const selectedRef = useRef<Booking | null>(null);
  selectedRef.current = selected;
  const quoteRequestGeneration = useRef(0);
  const cancellationAttempt = useRef<BookingCancelBody | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [exactCancellationRetryRequired, setExactCancellationRetryRequired] =
    useState(false);
  // Synchronous in-flight flag for the cancel mutation. State (`cancelling`) and
  // `disabled` only update on the NEXT render, so a same-tick double-click would
  // pass the state guard twice; a ref flips immediately and blocks the 2nd POST.
  const cancelInFlight = useRef(false);

  // --- No-show state ---------------------------------------------------------
  // Whether the operator holds facility:schedule — SERVER-COMPUTED via
  // checkFacilityPermission, never derived from the user object client-side.
  // Gates the "Mark No-Show" action on a confirmed booking.
  const [canNoShow, setCanNoShow] = useState(false);
  const [markingNoShow, setMarkingNoShow] = useState(false);
  // Synchronous in-flight flag for the no-show mutation (same double-click guard
  // as the cancel flow — the ref flips before any await).
  const noShowInFlight = useRef(false);

  // --- Cancel confirm-step state ---------------------------------------------
  // Which sub-view of the detail dialog is showing. Reset to "detail" whenever a
  // new booking is selected (see openDetail) so the confirm step never leaks
  // across bookings.
  const [dialogStep, setDialogStep] = useState<DialogStep>("detail");
  const [cancellationQuote, setCancellationQuote] =
    useState<CancellationQuoteState>({ status: "idle" });
  const [quoteNotice, setQuoteNotice] = useState<string | null>(null);
  // Whether the operator holds facility:refund_override — SERVER-COMPUTED via
  // checkFacilityPermission, never derived from the user object client-side.
  const [canOverride, setCanOverride] = useState(false);
  // The optional operator note (max 500). Required only on a deviating override.
  const [reason, setReason] = useState("");
  // The adjustable refund as a raw string (only meaningful when canOverride).
  // Defaults to the quoted policy refund once the server decision lands.
  const [refundInput, setRefundInput] = useState("");
  // A view-level validation message (bounds / reason-required). The backend
  // re-enforces all of these; this is purely an early, honest guardrail.
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [, setQuoteExpiryTick] = useState(0);

  const bookingLoadGeneration = useRef(0);
  const bookingScopeKey = JSON.stringify({
    facilityId,
    fromDate,
    toDate,
    selectedFacilityIds,
    selectedStatuses,
    committedSearch,
    sortBy,
    sortDirection,
    page,
  });
  const bookingScope = useRef(bookingScopeKey);
  bookingScope.current = bookingScopeKey;
  const bookingLoadInFlight = useRef<{
    scope: string;
    promise: Promise<boolean>;
  } | null>(null);

  const resetCancellationActions = useCallback(() => {
    quoteRequestGeneration.current += 1;
    cancellationAttempt.current = null;
    setExactCancellationRetryRequired(false);
    setDialogStep((step) => (step === "confirm" ? "detail" : step));
    setCancellationQuote({ status: "idle" });
    setQuoteNotice(null);
    setConfirmError(null);
    setReason("");
    setRefundInput("");
    setCanOverride(false);
  }, []);

  const resetSelectedActions = useCallback(() => {
    resetCancellationActions();
    setDialogStep("detail");
    setCanNoShow(false);
  }, [resetCancellationActions]);

  // Publish the day's canonical response as one React update. If the operator
  // has a detail dialog open, replace its row object from the same response so
  // a player-side cancellation cannot leave confirmed-only actions behind.
  const publishBookings = useCallback(
    (result: Booking[]): void => {
      setBookings(result);
      const current = selectedRef.current;
      if (!current) return;

      const canonical =
        result.find((booking) => booking.booking_id === current.booking_id) ??
        null;
      selectedRef.current = canonical;
      setSelected(canonical);
      if (canonical === null || canonical.status !== "confirmed") {
        resetSelectedActions();
      } else if (!canonical.cancellation_allowed) {
        resetCancellationActions();
      }
    },
    [resetCancellationActions, resetSelectedActions],
  );

  const loadBookings = useCallback((): Promise<boolean> => {
    if (!facilityId) return Promise.resolve(false);
    const scope = bookingScopeKey;
    if (bookingLoadInFlight.current?.scope === scope) {
      return bookingLoadInFlight.current.promise;
    }

    const generation = ++bookingLoadGeneration.current;
    const isCurrent = (): boolean =>
      generation === bookingLoadGeneration.current &&
      bookingScope.current === scope;

    const request = (async (): Promise<boolean> => {
      try {
        const result = await searchReservationOperations(facilityId, {
          fromDate,
          toDate,
          facilityIds:
            selectedFacilityIds.length > 0 ? selectedFacilityIds : undefined,
          statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
          query: committedSearch || undefined,
          sortBy,
          sortDirection,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        });
        if (!isCurrent()) return false;
        publishBookings(result.items);
        setTotal(result.total);
        return true;
      } catch (err) {
        if (!isCurrent()) return false;
        throw err;
      }
    })();
    bookingLoadInFlight.current = { scope, promise: request };
    const clearInFlight = () => {
      if (bookingLoadInFlight.current?.promise === request) {
        bookingLoadInFlight.current = null;
      }
    };
    void request.then(clearInFlight, clearInFlight);
    return request;
  }, [
    bookingScopeKey,
    committedSearch,
    facilityId,
    fromDate,
    page,
    publishBookings,
    selectedFacilityIds,
    selectedStatuses,
    sortBy,
    sortDirection,
    toDate,
  ]);

  const loadBookingsFresh = useCallback(async (): Promise<boolean> => {
    const previous = bookingLoadInFlight.current;
    if (previous?.scope === bookingScopeKey) {
      try {
        await previous.promise;
      } catch {
        // A failed older read must not prevent the required canonical read.
      }
    }
    return loadBookings();
  }, [bookingScopeKey, loadBookings]);

  const reconcileStartedCancellation = useCallback(
    async (bookingId: string): Promise<void> => {
      const current = selectedRef.current;
      if (current?.booking_id === bookingId) {
        const blocked: Booking = {
          ...current,
          cancellation_allowed: false,
          cancellation_unavailable_reason: "already_started",
        };
        selectedRef.current = blocked;
        setSelected(blocked);
        setBookings((rows) =>
          rows.map((row) => (row.booking_id === bookingId ? blocked : row)),
        );
      }
      resetCancellationActions();
      if (!facilityId) return;

      try {
        await loadBookingsFresh();
      } catch (refreshError) {
        setError(
          refreshError instanceof Error
            ? refreshError.message
            : "Failed to refresh this booking.",
        );
      }
    },
    [facilityId, loadBookingsFresh, resetCancellationActions],
  );

  // Bookings reload whenever the resolved facility or selected day changes.
  useEffect(() => {
    if (!facilityId || pageNeedsCanonicalization) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [, courtList] = await Promise.all([
          loadBookings(),
          listCourts(facilityId).catch(() => [] as Court[]),
        ]);
        if (!cancelled) setCourts(courtList);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load bookings.",
          );
          setBookings([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      bookingLoadGeneration.current += 1;
    };
  }, [facilityId, loadBookings, pageNeedsCanonicalization]);

  const reconcileBookings = useCallback(async () => {
    if (!facilityId) return;
    try {
      const applied = await loadBookings();
      if (applied) setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to refresh bookings.",
      );
      throw err;
    }
  }, [facilityId, loadBookings]);
  useReservationStateStream({
    facilityId: facilityId ?? "",
    revision: 0,
    onReconcile: reconcileBookings,
    enabled: Boolean(facilityId),
  });

  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    let timer: number | undefined;
    const schedule = () => {
      timer = window.setTimeout(async () => {
        try {
          await reconcileBookings();
        } catch {
          // reconcileBookings already publishes the operator-facing error.
        } finally {
          if (!cancelled) schedule();
        }
      }, 15_000);
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [facilityId, reconcileBookings]);

  // The quoted refund is the single source of truth for the override default and
  // deviation check. A selected-row amount is never substituted for quote data.
  const policyRefund = useMemo(
    () =>
      cancellationQuote.status === "ready"
        ? parseDecimal(cancellationQuote.quote.refund_amount)
        : null,
    [cancellationQuote],
  );

  const quoteExpired =
    cancellationQuote.status === "ready" &&
    isBookingCancellationQuoteExpired(cancellationQuote.quote);

  useEffect(() => {
    if (cancellationQuote.status !== "ready" || quoteExpired) return;
    const expiresAt = Date.parse(cancellationQuote.quote.expires_at);
    const delay = Math.min(expiresAt - Date.now() + 25, 2_147_483_647);
    const timer = window.setTimeout(
      () => setQuoteExpiryTick((value) => value + 1),
      delay,
    );
    return () => window.clearTimeout(timer);
  }, [cancellationQuote, quoteExpired]);

  const loadCancellationQuote = useCallback(
    async (booking: Booking, decisionChanged: boolean): Promise<void> => {
      const generation = ++quoteRequestGeneration.current;
      cancellationAttempt.current = null;
      setExactCancellationRetryRequired(false);
      setCancellationQuote({ status: "loading" });
      setRefundInput("");
      setQuoteNotice(
        decisionChanged
          ? "Cancellation terms changed. Loading the updated decision…"
          : null,
      );

      try {
        const quote = await createBookingCancellationQuote(booking.booking_id);
        if (
          generation !== quoteRequestGeneration.current ||
          selectedRef.current?.booking_id !== booking.booking_id
        ) {
          return;
        }
        if (!isValidBookingCancellationQuote(quote, booking.booking_id)) {
          setCancellationQuote({ status: "error" });
          setQuoteNotice(
            decisionChanged
              ? "Cancellation terms changed, but the updated decision was incomplete. Try again."
              : "Couldn't load a complete cancellation decision. Try again.",
          );
          return;
        }
        if (isBookingCancellationQuoteExpired(quote)) {
          setCancellationQuote({ status: "error" });
          setQuoteNotice(
            "The cancellation decision expired before it could be reviewed. Try again.",
          );
          return;
        }

        setCancellationQuote({ status: "ready", quote });
        setRefundInput(String(quote.refund_amount));
        setQuoteNotice(
          decisionChanged
            ? "Cancellation terms changed. Review the updated amounts and confirm again."
            : null,
        );
      } catch (err) {
        if (
          generation !== quoteRequestGeneration.current ||
          selectedRef.current?.booking_id !== booking.booking_id
        ) {
          return;
        }

        if (
          isApiError(err) &&
          err.status === 409 &&
          err.code === "BOOKING_CANCELLATION_ALREADY_STARTED"
        ) {
          await reconcileStartedCancellation(booking.booking_id);
          return;
        }

        setCancellationQuote({ status: "error" });
        setQuoteNotice(
          decisionChanged
            ? "Cancellation terms changed, but the updated decision couldn't be loaded. Try again."
            : "Couldn't load the cancellation decision. Try again.",
        );
      }
    },
    [reconcileStartedCancellation],
  );

  // Open a booking's detail dialog. Always lands on the "detail" step and clears
  // any stale confirm-step state from a previously-selected booking. It also asks
  // the server whether the operator may mark a no-show (facility:schedule) so the
  // "Mark No-Show" action renders only when the server ALLOWS it AND the booking
  // is confirmed — the answer is SERVER-COMPUTED, never derived from the user
  // object. The check fires for every booking (not just confirmed) so the gate is
  // a single, uniform server round-trip regardless of status.
  async function retryCancellation() {
    const target = selectedRef.current;
    if (
      !target?.cancellation_recovery_allowed ||
      !target.cancellation_recovery_pending ||
      !scheduleFacilityIds.has(target.facility_id) ||
      cancelInFlight.current
    )
      return;
    const scope = bookingScope.current;
    cancelInFlight.current = true;
    setCancelling(true);
    clearErrorFeedback();
    try {
      await recoverBookingCancellation(target.booking_id);
      if (
        bookingScope.current !== scope ||
        selectedRef.current?.booking_id !== target.booking_id
      )
        return;
      await reconcileBookings();
    } catch {
      if (
        bookingScope.current !== scope ||
        selectedRef.current?.booking_id !== target.booking_id
      )
        return;
      setFeedback({
        severity: "error",
        message:
          "Couldn't finish the cancellation. Try again to resume the same cancellation.",
      });
    } finally {
      cancelInFlight.current = false;
      setCancelling(false);
    }
  }

  const openDetail = (booking: Booking) => {
    clearErrorFeedback();
    quoteRequestGeneration.current += 1;
    cancellationAttempt.current = null;
    setExactCancellationRetryRequired(false);
    setSelected(booking);
    setDialogStep("detail");
    setReason("");
    setRefundInput("");
    setConfirmError(null);
    setCancellationQuote({ status: "idle" });
    setQuoteNotice(null);
    setCanNoShow(false);

    if (booking.facility_id) {
      checkFacilityPermission(booking.facility_id, NO_SHOW_ACTION)
        .then((res) => {
          // Drop a stale answer if the operator already moved to another booking.
          if (selectedRef.current?.booking_id !== booking.booking_id) return;
          setCanNoShow(Boolean(res?.allowed));
        })
        .catch(() => {
          if (selectedRef.current?.booking_id !== booking.booking_id) return;
          setCanNoShow(false);
        });
    }
  };

  // Close the dialog entirely. MUI Dialog owns Escape/backdrop dismissal and
  // restores focus to the row selector that opened it.
  const closeDialog = useCallback(
    (force = false) => {
      if (!force && (cancelInFlight.current || exactCancellationRetryRequired))
        return;
      quoteRequestGeneration.current += 1;
      cancellationAttempt.current = null;
      setExactCancellationRetryRequired(false);
      setSelected(null);
      setDialogStep("detail");
      setCancellationQuote({ status: "idle" });
      setQuoteNotice(null);
      setCanNoShow(false);
      clearErrorFeedback();
    },
    [clearErrorFeedback, exactCancellationRetryRequired],
  );

  // Entering confirmation always requests a fresh server decision, including for
  // zero-dollar/offline court blocks. Confirmation stays disabled until it lands.
  const enterConfirmStep = useCallback(() => {
    if (!selected || !selected.cancellation_allowed) return;
    setDialogStep("confirm");
    setConfirmError(null);
    setReason("");
    setRefundInput("");
    setCanOverride(false);

    const booking = selected;
    // Permission check — server-computed; the override control renders iff allowed.
    // Drop a stale answer if the operator switched bookings mid-flight. The quote
    // request has a stronger booking + generation guard of its own.
    checkFacilityPermission(booking.facility_id, REFUND_OVERRIDE_ACTION)
      .then((res) => {
        if (selectedRef.current?.booking_id !== booking.booking_id) return;
        setCanOverride(Boolean(res?.allowed));
      })
      .catch(() => {
        if (selectedRef.current?.booking_id !== booking.booking_id) return;
        setCanOverride(false);
      });

    void loadCancellationQuote(booking, false);
  }, [selected, loadCancellationQuote]);

  // Return to the detail view without cancelling (Back / dismiss).
  const backToDetail = () => {
    if (cancelInFlight.current || exactCancellationRetryRequired) return;
    clearErrorFeedback();
    quoteRequestGeneration.current += 1;
    cancellationAttempt.current = null;
    setExactCancellationRetryRequired(false);
    setDialogStep("detail");
    setCancellationQuote({ status: "idle" });
    setQuoteNotice(null);
    setConfirmError(null);
  };

  const handleConfirmCancel = async () => {
    // Synchronous re-entrancy guard: `disabled={cancelling}` only applies on the
    // NEXT render, so a same-tick double-click would otherwise fire two POSTs
    // (the 2nd 409s and shows a spurious error toast after the success toast).
    // The ref flips immediately, before any await, so the 2nd dispatch no-ops.
    if (cancelInFlight.current) return;
    if (!selected || !facilityId) return;
    if (
      cancellationQuote.status !== "ready" ||
      cancellationQuote.quote.booking_id !== selected.booking_id
    ) {
      setConfirmError(
        "Load a current cancellation decision before confirming.",
      );
      return;
    }

    const quote = cancellationQuote.quote;
    let request = cancellationAttempt.current;

    if (!request) {
      if (isBookingCancellationQuoteExpired(quote)) {
        setConfirmError(
          "This cancellation decision expired. Refresh it before confirming.",
        );
        return;
      }

      const amountCharged = parseDecimal(quote.amount_charged);

      // Resolve the override the operator intends to send. We only ever send an
      // override when the operator HOLDS the permission AND the value deviates from
      // the policy refund — an unchanged value (or a non-privileged operator) sends
      // nothing, so the backend cancels at policy.
      let overrideToSend: Money | undefined;
      if (canOverride) {
        const parsed = parseDecimal(refundInput);
        if (parsed === null) {
          setConfirmError("Enter a valid refund amount.");
          return;
        }
        if (parsed < 0) {
          setConfirmError("Refund cannot be negative.");
          return;
        }
        if (amountCharged !== null && parsed > amountCharged) {
          setConfirmError(
            `Refund cannot exceed the amount charged (${formatMoney(
              quote.amount_charged,
              quote.currency,
            )}).`,
          );
          return;
        }
        const deviates =
          policyRefund === null || Math.abs(parsed - policyRefund) > 0.0001;
        if (deviates) {
          if (!reason.trim()) {
            setConfirmError(
              "A reason is required when you change the refund amount.",
            );
            return;
          }
          overrideToSend = asMoney(String(parsed)) ?? undefined;
        }
      }

      request = {
        quote_id: quote.quote_id,
        idempotency_key: globalThis.crypto.randomUUID(),
        reason: reason.trim() || null,
        refund_amount_override: overrideToSend,
      };
      cancellationAttempt.current = request;
      setExactCancellationRetryRequired(false);
    }

    setConfirmError(null);
    cancelInFlight.current = true;
    setCancelling(true);
    try {
      clearErrorFeedback();
      // Preserve the complete body, not only its key: an uncertain response must
      // retry the exact quote, refund override, and reason that were dispatched.
      const updated = await cancelBooking(selected.booking_id, request);
      // On success, re-list from the server (the refetch is the source of truth —
      // no optimistic flip), surface the ACTUAL refund, then close the dialog.
      await loadBookingsFresh();
      const refunded = formatMoney(updated.refund_amount, updated.currency);
      setFeedback({
        severity: "success",
        message:
          refunded === "—"
            ? "Booking cancelled"
            : `Booking cancelled — refunded ${refunded}`,
      });
      closeDialog(true);
    } catch (err) {
      if (
        isApiError(err) &&
        err.status === 409 &&
        err.code === "BOOKING_CANCELLATION_ALREADY_STARTED"
      ) {
        await reconcileStartedCancellation(selected.booking_id);
        return;
      }
      if (isApiError(err) && err.details?.refresh_required === true) {
        cancellationAttempt.current = null;
        setExactCancellationRetryRequired(false);
        try {
          await loadBookingsFresh();
        } catch (refreshError) {
          setFeedback({
            severity: "error",
            message:
              refreshError instanceof Error
                ? refreshError.message
                : "Failed to refresh this booking.",
          });
          return;
        }
        const canonical = selectedRef.current;
        if (
          canonical?.booking_id === selected.booking_id &&
          canonical.status === "confirmed"
        ) {
          await loadCancellationQuote(canonical, true);
        }
        return;
      }
      if (requiresExactCancellationRetry(err)) {
        setExactCancellationRetryRequired(true);
        setConfirmError(
          "Cancellation status is still being reconciled. Retry without changing the refund or note.",
        );
      } else {
        cancellationAttempt.current = null;
        setExactCancellationRetryRequired(false);
      }
      // Rejected (403 RBAC / 400 bad override / 409 confirmed-only): surface
      // honestly, STAY on the confirm step, and leave the list byte-for-byte
      // unchanged — NO refetch that could mask the failure.
      setFeedback({
        severity: "error",
        message:
          err instanceof Error ? err.message : "Failed to cancel this booking.",
      });
    } finally {
      cancelInFlight.current = false;
      setCancelling(false);
    }
  };

  // Advance the open booking to the no-show CONFIRM step. This does NOT mark a
  // no-show — it only shows the honest no-refund copy and a confirm button. There
  // deliberately requests NO cancellation quote: a no-show forfeits the
  // charge, so there is no refund to compute or display.
  const enterNoShowConfirm = () => {
    if (!selected) return;
    setDialogStep("noshow_confirm");
    setConfirmError(null);
  };

  // Return to detail without marking a no-show (Back / dismiss).
  const backToDetailFromNoShow = () => {
    if (noShowInFlight.current) return;
    clearErrorFeedback();
    setDialogStep("detail");
    setConfirmError(null);
  };

  const handleConfirmNoShow = async () => {
    // Synchronous re-entrancy guard — the ref flips before any await, blocking a
    // same-tick double-click that the `disabled` state would only stop next render.
    if (noShowInFlight.current) return;
    if (!selected || !facilityId) return;

    noShowInFlight.current = true;
    setMarkingNoShow(true);
    try {
      clearErrorFeedback();
      // Confirmed-only + RBAC are enforced by the backend. The endpoint takes no
      // inputs (a no-show forfeits the charge — no money moves), so the body is empty.
      await markNoShow(selected.booking_id);
      // On success, RE-LIST from the server (the refetch is the source of truth —
      // NO optimistic flip; the row shows no_show only after the refetch resolves),
      // surface a success toast, then close the dialog.
      await loadBookingsFresh();
      setFeedback({
        severity: "success",
        message: "Booking marked as no-show",
      });
      closeDialog();
    } catch (err) {
      if (isApiError(err) && err.details?.refresh_required === true) {
        try {
          await loadBookingsFresh();
        } catch (refreshError) {
          setFeedback({
            severity: "error",
            message:
              refreshError instanceof Error
                ? refreshError.message
                : "Failed to refresh this booking.",
          });
        }
        return;
      }
      // Rejected (403 RBAC / 400 confirmed-only): surface honestly, STAY on the
      // confirm step, and leave the list byte-for-byte unchanged — NO refetch that
      // could mask the failure.
      setFeedback({
        severity: "error",
        message:
          err instanceof Error
            ? err.message
            : "Failed to mark this booking as no-show.",
      });
    } finally {
      noShowInFlight.current = false;
      setMarkingNoShow(false);
    }
  };

  const setDateRange = (nextFrom: Date, nextTo: Date) => {
    updateSearchParams((next) => {
      next.set("from", format(nextFrom, "yyyy-MM-dd"));
      next.set("to", format(nextTo, "yyyy-MM-dd"));
      next.delete("page");
    });
  };

  const applyPreset = (
    preset: "today" | "yesterday" | "this_week" | "last_week",
  ) => {
    if (preset === "today") {
      setDateRange(facilityToday, facilityToday);
      return;
    }
    if (preset === "yesterday") {
      const yesterday = subDays(facilityToday, 1);
      setDateRange(yesterday, yesterday);
      return;
    }
    const week =
      preset === "last_week" ? subWeeks(facilityToday, 1) : facilityToday;
    setDateRange(
      startOfWeek(week, { weekStartsOn: 1 }),
      endOfWeek(week, { weekStartsOn: 1 }),
    );
  };

  const updateDateInput = (key: "from" | "to", value: string) => {
    if (!value) return;
    updateSearchParams((next) => {
      next.set(key, value);
      const otherKey = key === "from" ? "to" : "from";
      const other = validIsoDate(next.get(otherKey), value);
      if (key === "from" && value > other) next.set("to", value);
      if (key === "to" && value < other) next.set("from", value);
      next.delete("page");
    });
  };

  const toggleFacility = (id: string) => {
    const next = selectedFacilityIds.includes(id)
      ? selectedFacilityIds.filter((value) => value !== id)
      : [...selectedFacilityIds, id];
    setRepeatedFilter("facility", next);
  };

  const facilityFilterLabel =
    selectedFacilityIds.length === 0
      ? "All authorized facilities"
      : selectedFacilityIds.length === 1
        ? (facilityOptions.find(
            (item) => item.facility_id === selectedFacilityIds[0],
          )?.name ?? "1 facility")
        : `${selectedFacilityIds.length} facilities`;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const reachableTotalPages = Math.min(totalPages, MAX_PAGE);

  useEffect(() => {
    if (!loading && page > reachableTotalPages) {
      updateSearchParams((next) =>
        next.set("page", String(reachableTotalPages)),
      );
    }
  }, [loading, page, reachableTotalPages, updateSearchParams]);

  // Brief resolve window: the FacilityContext selection hasn't landed yet. Show a
  // loading affordance and fire no reads (the Layer-3 gate owns the genuine
  // no-facility case).
  if (facilityLoading) {
    return (
      <Stack
        role="status"
        direction="row"
        spacing={1}
        sx={{
          minHeight: "40vh",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, lg: 3 },
        }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }
  if (!facilityId) {
    return (
      <Box sx={{ px: { xs: 2, lg: 3 }, py: 4 }}>
        <Typography color="text.secondary" variant="body2">
          Reservation access is not available for your assigned facilities.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 } }}>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        sx={{
          alignItems: { lg: "flex-start" },
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography component="h1" variant="h5">
            Reservations
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Search and manage reservations across authorized facilities.
          </Typography>
        </Box>
        <Stack
          component="section"
          aria-label="Reservation actions"
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          useFlexGap
          sx={{
            alignItems: { sm: "center" },
            flexWrap: { sm: "wrap", lg: "nowrap" },
            justifyContent: { lg: "flex-end" },
          }}
        >
          {canScheduleAnchor ? (
            <>
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{ width: { sm: "100%", lg: "auto" } }}
              >
                Actions for {facility.name}
              </Typography>
              <Button
                ref={createTriggerRef}
                size="small"
                variant="contained"
                startIcon={<AddRoundedIcon aria-hidden="true" />}
                onClick={() => setCreateOpen(true)}
                sx={{ whiteSpace: "nowrap" }}
              >
                Create reservation
              </Button>
              <Button
                ref={blockTriggerRef}
                size="small"
                variant="outlined"
                startIcon={<AddRoundedIcon aria-hidden="true" />}
                onClick={() => setBlockOpen(true)}
                sx={{ whiteSpace: "nowrap" }}
              >
                Block court / event
              </Button>
            </>
          ) : (
            <Typography color="text.secondary" variant="caption">
              Reservation actions are not available for the selected facility.
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack
        component="section"
        aria-label="Reservation filters"
        spacing={2}
        sx={{
          py: 2,
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: "wrap", alignItems: "flex-end" }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            aria-label="Date presets"
            sx={{ flexWrap: "wrap" }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => applyPreset("today")}
            >
              Today
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => applyPreset("yesterday")}
            >
              Yesterday
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => applyPreset("this_week")}
            >
              This week
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => applyPreset("last_week")}
            >
              Last week
            </Button>
          </Stack>
          <TextField
            type="date"
            size="small"
            label="From"
            value={fromDate}
            onChange={(event) => updateDateInput("from", event.target.value)}
            slotProps={{ htmlInput: { "aria-label": "From date" } }}
            sx={{ width: 160 }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={toDate}
            onChange={(event) => updateDateInput("to", event.target.value)}
            slotProps={{ htmlInput: { "aria-label": "To date" } }}
            sx={{ width: 160 }}
          />
          <Button
            variant="outlined"
            size="small"
            endIcon={<ExpandMoreRoundedIcon aria-hidden="true" />}
            aria-haspopup="menu"
            aria-expanded={Boolean(facilityMenuAnchor)}
            onClick={(event) => setFacilityMenuAnchor(event.currentTarget)}
            sx={{
              maxWidth: { xs: "100%", sm: 360 },
              minWidth: 0,
              justifyContent: "space-between",
              overflowWrap: "anywhere",
              textAlign: "left",
              whiteSpace: "normal",
              "& .MuiButton-endIcon": { flexShrink: 0 },
            }}
          >
            {facilityFilterLabel}
          </Button>
          <Menu
            anchorEl={facilityMenuAnchor}
            open={Boolean(facilityMenuAnchor)}
            onClose={() => setFacilityMenuAnchor(null)}
            slotProps={{
              paper: { sx: { width: "min(28rem, calc(100vw - 2rem))" } },
            }}
          >
            <MenuItem
              role="menuitemcheckbox"
              aria-checked={selectedFacilityIds.length === 0}
              onClick={() => setRepeatedFilter("facility", [])}
            >
              <Checkbox
                checked={selectedFacilityIds.length === 0}
                tabIndex={-1}
                aria-hidden="true"
              />
              All authorized facilities
            </MenuItem>
            {facilityOptions.map((item) => (
              <MenuItem
                key={item.facility_id}
                role="menuitemcheckbox"
                aria-checked={selectedFacilityIds.includes(item.facility_id)}
                onClick={() => toggleFacility(item.facility_id)}
              >
                <Checkbox
                  checked={selectedFacilityIds.includes(item.facility_id)}
                  tabIndex={-1}
                  aria-hidden="true"
                />
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                  {item.name}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="reservation-sort-label">Sort</InputLabel>
            <Select
              labelId="reservation-sort-label"
              label="Sort"
              aria-label="Sort reservations"
              value={`${sortBy}:${sortDirection}`}
              onChange={(event) => {
                const [nextSort, nextDirection] = event.target.value.split(":");
                updateSearchParams((next) => {
                  next.set("sort", nextSort);
                  next.set("direction", nextDirection);
                  next.delete("page");
                });
              }}
            >
              <MenuItem value="start_time:asc">Start time, earliest</MenuItem>
              <MenuItem value="start_time:desc">Start time, latest</MenuItem>
              <MenuItem value="facility:asc">Facility name</MenuItem>
              <MenuItem value="player:asc">Player name</MenuItem>
              <MenuItem value="created_at:desc">Recently created</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          sx={{ alignItems: { lg: "center" }, justifyContent: "space-between" }}
        >
          <ToggleButtonGroup
            exclusive={false}
            value={selectedStatuses.length === 0 ? ["all"] : selectedStatuses}
            onChange={(_event, nextValues: StatusFilter[]) => {
              const nextStatuses = nextValues.filter(
                (value): value is BookingStatusFilter => value !== "all",
              );
              if (selectedStatuses.length > 0 && nextValues.includes("all")) {
                setRepeatedFilter("status", []);
                return;
              }
              setRepeatedFilter("status", nextStatuses);
            }}
            aria-label="Reservation status"
            size="small"
            sx={{
              flexWrap: "wrap",
              gap: 0.75,
              "& .MuiToggleButtonGroup-grouped": {
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                m: 0,
              },
            }}
          >
            <ToggleButton
              value="all"
              aria-label="All"
              aria-pressed={selectedStatuses.length === 0}
            >
              All
            </ToggleButton>
            {STATUS_FILTERS.filter((item) => item.value !== "all").map(
              (item) => (
                <ToggleButton
                  key={item.value}
                  value={item.value}
                  aria-label={item.label}
                  aria-pressed={selectedStatuses.includes(item.value)}
                >
                  {item.label}
                </ToggleButton>
              ),
            )}
          </ToggleButtonGroup>
          <Stack
            component="form"
            direction="row"
            spacing={1}
            onSubmit={(event) => {
              event.preventDefault();
              updateSearchParams((next) => {
                const value = searchDraft.trim();
                if (value) next.set("q", value);
                else next.delete("q");
                next.delete("page");
              });
            }}
            sx={{ width: "100%", maxWidth: { lg: 480 } }}
          >
            <TextField
              type="search"
              size="small"
              fullWidth
              placeholder="Player, email, booking ID, or court"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              slotProps={{
                htmlInput: { "aria-label": "Search reservations" },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" aria-hidden="true" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button type="submit" size="small" variant="contained">
              Search
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!error ? (
        <Stack
          component="section"
          aria-label="Reservation results"
          spacing={1.5}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{
              alignItems: { sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography component="h2" variant="subtitle1">
                Matching reservations
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {total.toLocaleString()} matching reservation
                {total === 1 ? "" : "s"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
              <CalendarMonthOutlinedIcon
                fontSize="small"
                color="action"
                aria-hidden="true"
              />
              <Typography color="text.secondary" variant="caption">
                {fromDate === toDate ? fromDate : `${fromDate} to ${toDate}`}
              </Typography>
            </Stack>
          </Stack>

          <ReservationOperationsTable
            items={bookings}
            total={total}
            isLoading={loading}
            onSelect={openDetail}
          />
          {totalPages > reachableTotalPages ? (
            <Alert severity="warning">
              Only the first {MAX_REACHABLE_RESULTS.toLocaleString()} matches
              are available. Narrow filters to reach later results.
            </Alert>
          ) : null}

          <Stack
            direction="row"
            spacing={1.5}
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <Button
              variant="outlined"
              size="small"
              disabled={page <= 1}
              onClick={() =>
                updateSearchParams((next) => next.set("page", String(page - 1)))
              }
            >
              Previous
            </Button>
            <Typography color="text.secondary" variant="body2">
              Page {Math.min(page, reachableTotalPages)} of {totalPages}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={page >= reachableTotalPages}
              onClick={() =>
                updateSearchParams((next) => next.set("page", String(page + 1)))
              }
            >
              Next
            </Button>
          </Stack>
        </Stack>
      ) : null}

      <Dialog
        open={Boolean(selected)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="reservation-dialog-title"
        onClose={() => {
          if (
            !cancelling &&
            !markingNoShow &&
            !exactCancellationRetryRequired
          ) {
            closeDialog();
          }
        }}
      >
        {selected && dialogStep === "detail" ? (
          <>
            <DialogTitle
              id="reservation-dialog-title"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                {isCourtBlock(selected)
                  ? selected.event_name?.trim() || "Court block"
                  : selected.display_name?.trim() || "Reserved"}
              </Box>
              {!cancelling &&
              !markingNoShow &&
              !exactCancellationRetryRequired ? (
                <IconButton
                  autoFocus
                  aria-label="Close reservation details"
                  edge="end"
                  size="small"
                  onClick={() => closeDialog()}
                >
                  <CloseRoundedIcon aria-hidden="true" />
                </IconButton>
              ) : null}
            </DialogTitle>
            <DialogContent dividers>
              {feedback?.severity === "error" ? (
                <Alert
                  severity="error"
                  onClose={() => setFeedback(null)}
                  sx={{ mb: 2 }}
                >
                  {feedback.message}
                </Alert>
              ) : null}
              <DialogContentText sx={{ mb: 2 }}>
                {isCourtBlock(selected)
                  ? "Court block / event"
                  : "Booking detail"}
              </DialogContentText>
              <Box
                component="dl"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto minmax(0, 1fr)",
                  columnGap: 3,
                  rowGap: 1,
                  m: 0,
                }}
              >
                <Typography
                  component="dt"
                  color="text.secondary"
                  variant="body2"
                >
                  Facility
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, fontWeight: 600 }}
                >
                  {selected.facility_name}
                </Typography>
                <Typography
                  component="dt"
                  color="text.secondary"
                  variant="body2"
                >
                  Court
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, fontWeight: 600 }}
                >
                  {selected.court_name}
                </Typography>
                <Typography
                  component="dt"
                  color="text.secondary"
                  variant="body2"
                >
                  When
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {formatBookingDateTime(
                    selected.start_time,
                    bookingDateTimeFormatters,
                  )}{" "}
                  –{" "}
                  {formatBookingTime(
                    selected.end_time,
                    bookingDateTimeFormatters,
                  )}
                </Typography>
                <Typography
                  component="dt"
                  color="text.secondary"
                  variant="body2"
                >
                  Amount
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, fontWeight: 600 }}
                >
                  {isCourtBlock(selected)
                    ? "— (no charge)"
                    : formatMoney(selected.amount_charged, selected.currency)}
                </Typography>
                <Typography
                  component="dt"
                  color="text.secondary"
                  variant="body2"
                >
                  Status
                </Typography>
                <Box component="dd" sx={{ m: 0 }}>
                  <Chip
                    label={
                      selected.cancellation_recovery_pending
                        ? "Cancellation processing"
                        : humanizeStatus(selected.status)
                    }
                    color={
                      selected.cancellation_recovery_pending
                        ? "warning"
                        : bookingStatusColor(selected.status)
                    }
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
              {selected.status === "confirmed" ? (
                selected.cancellation_unavailable_reason ===
                "already_started" ? (
                  <Typography
                    role="status"
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 2 }}
                  >
                    This booking has already started. Cancellation is no longer
                    available.
                  </Typography>
                ) : selected.cancellation_unavailable_reason ===
                  "cancellation_recovery_pending" ? (
                  <Typography
                    role="status"
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 2 }}
                  >
                    {selected.cancellation_recovery_allowed
                      ? "Cancellation is processing. Retry to resume the same cancellation."
                      : "Cancellation is processing. Recovery is handled in staff court administration."}
                  </Typography>
                ) : !selected.cancellation_allowed ? (
                  <Typography
                    role="status"
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 2 }}
                  >
                    Cancellation is not available for this booking.
                  </Typography>
                ) : null
              ) : null}
              {selected.cancellation_recovery_pending &&
              selected.cancellation_recovery_allowed &&
              scheduleFacilityIds.has(selected.facility_id) ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={cancelling}
                  sx={{ mt: 2 }}
                  onClick={() => void retryCancellation()}
                >
                  {cancelling ? "Retrying cancellation…" : "Retry cancellation"}
                </Button>
              ) : null}
            </DialogContent>
            {/* Both actions remain confirmed-only. Cancellation additionally
                  renders only from the server-authored eligibility verdict;
                  no-show remains independently permission-gated so a started
                  booking can still follow the supported operational workflow. */}
            {scheduleFacilityIds.has(selected.facility_id) &&
            selected.status === "confirmed" &&
            !selected.cancellation_recovery_pending &&
            (selected.cancellation_allowed ||
              (canNoShow && !isCourtBlock(selected))) ? (
              <DialogActions>
                {/* No-Show is a player concept — a court block has no player, so
                      the action is suppressed for blocks (#1674). */}
                {canNoShow && !isCourtBlock(selected) ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={enterNoShowConfirm}
                  >
                    Mark No-Show
                  </Button>
                ) : null}
                {selected.cancellation_allowed ? (
                  <Button
                    color="error"
                    variant="contained"
                    size="small"
                    onClick={enterConfirmStep}
                  >
                    Cancel Booking
                  </Button>
                ) : null}
              </DialogActions>
            ) : null}
          </>
        ) : null}

        {selected && dialogStep === "confirm" ? (
          <>
            <DialogTitle id="reservation-dialog-title">
              Cancel this booking?
            </DialogTitle>
            <DialogContent dividers>
              {feedback?.severity === "error" ? (
                <Alert
                  severity="error"
                  onClose={() => setFeedback(null)}
                  sx={{ mb: 2 }}
                >
                  {feedback.message}
                </Alert>
              ) : null}
              <DialogContentText sx={{ mb: 2 }}>
                Review the refund, add an optional note, then confirm.
              </DialogContentText>
              <Stack spacing={2}>
                {cancellationQuote.status === "ready" ? (
                  <Box
                    component="dl"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "auto minmax(0, 1fr)",
                      columnGap: 3,
                      rowGap: 1,
                      m: 0,
                    }}
                  >
                    <Typography
                      component="dt"
                      color="text.secondary"
                      variant="body2"
                    >
                      Amount charged
                    </Typography>
                    <Typography
                      component="dd"
                      variant="body2"
                      sx={{ m: 0, fontWeight: 600 }}
                    >
                      {formatMoney(
                        cancellationQuote.quote.amount_charged,
                        cancellationQuote.quote.currency,
                      )}
                    </Typography>
                    <Typography
                      component="dt"
                      color="text.secondary"
                      variant="body2"
                    >
                      Cancellation fee
                    </Typography>
                    <Typography
                      component="dd"
                      variant="body2"
                      sx={{ m: 0, fontWeight: 600 }}
                    >
                      {formatMoney(
                        cancellationQuote.quote.fee_amount,
                        cancellationQuote.quote.currency,
                      )}
                    </Typography>
                    <Typography
                      component="dt"
                      color="text.secondary"
                      variant="body2"
                    >
                      Policy refund
                    </Typography>
                    <Typography
                      component="dd"
                      variant="body2"
                      sx={{ m: 0, fontWeight: 600 }}
                    >
                      {formatMoney(
                        cancellationQuote.quote.refund_amount,
                        cancellationQuote.quote.currency,
                      )}
                    </Typography>
                  </Box>
                ) : cancellationQuote.status === "loading" ||
                  cancellationQuote.status === "idle" ? (
                  <Stack
                    role="status"
                    aria-live="polite"
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <CircularProgress size={18} />
                    <Typography color="text.secondary" variant="body2">
                      Loading cancellation decision…
                    </Typography>
                  </Stack>
                ) : null}

                {quoteNotice ? (
                  <Alert
                    severity={
                      cancellationQuote.status === "error" ? "error" : "info"
                    }
                  >
                    {quoteNotice}
                  </Alert>
                ) : null}

                {quoteExpired && !exactCancellationRetryRequired ? (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ alignItems: { sm: "center" } }}
                  >
                    <Alert severity="error" sx={{ flex: 1 }}>
                      This cancellation decision expired. Refresh it before
                      confirming.
                    </Alert>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => void loadCancellationQuote(selected, true)}
                    >
                      Refresh decision
                    </Button>
                  </Stack>
                ) : null}

                {/* Operator-only adjustable refund — rendered iff the server
                      grants facility:refund_override. */}
                {canOverride && cancellationQuote.status === "ready" ? (
                  <Stack spacing={0.75}>
                    <TextField
                      id="refund-amount"
                      label="Refund amount"
                      type="number"
                      size="small"
                      value={refundInput}
                      disabled={cancelling || exactCancellationRetryRequired}
                      onChange={(event) => {
                        setRefundInput(event.target.value);
                        setConfirmError(null);
                        clearErrorFeedback();
                      }}
                      slotProps={{
                        htmlInput: {
                          "aria-label": "Refund amount",
                          inputMode: "decimal",
                          min: 0,
                          step: "0.01",
                        },
                      }}
                    />
                    <Typography color="text.secondary" variant="caption">
                      Add a note below if you change this from the policy
                      amount.
                    </Typography>
                  </Stack>
                ) : null}

                <TextField
                  id="cancel-reason"
                  label="Reason / note (optional)"
                  multiline
                  minRows={3}
                  value={reason}
                  disabled={cancelling || exactCancellationRetryRequired}
                  onChange={(event) => {
                    setReason(event.target.value);
                    setConfirmError(null);
                    clearErrorFeedback();
                  }}
                  slotProps={{
                    htmlInput: {
                      "aria-label": "Reason / note",
                      maxLength: 500,
                    },
                  }}
                />

                {confirmError ? (
                  <Alert severity="error">{confirmError}</Alert>
                ) : null}
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button
                autoFocus
                variant="outlined"
                size="small"
                onClick={backToDetail}
                disabled={cancelling || exactCancellationRetryRequired}
              >
                Back
              </Button>
              <Button
                color="error"
                variant="contained"
                size="small"
                onClick={handleConfirmCancel}
                disabled={
                  cancelling ||
                  cancellationQuote.status !== "ready" ||
                  (quoteExpired && !exactCancellationRetryRequired)
                }
              >
                {cancelling
                  ? "Cancelling…"
                  : exactCancellationRetryRequired
                    ? "Retry exact cancellation"
                    : "Confirm cancel"}
              </Button>
            </DialogActions>
          </>
        ) : null}

        {selected && dialogStep === "noshow_confirm" ? (
          <>
            <DialogTitle id="reservation-dialog-title">
              Mark this booking as a no-show?
            </DialogTitle>
            <DialogContent dividers>
              {feedback?.severity === "error" ? (
                <Alert
                  severity="error"
                  onClose={() => setFeedback(null)}
                  sx={{ mb: 2 }}
                >
                  {feedback.message}
                </Alert>
              ) : null}
              <DialogContentText sx={{ mb: 2 }}>
                The player did not show up. Review the amount below, then
                confirm.
              </DialogContentText>

              {/* Honest money copy: a no-show forfeits the charge. */}
              <Box
                component="dl"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto minmax(0, 1fr)",
                  columnGap: 3,
                  rowGap: 1,
                  m: 0,
                }}
              >
                <Typography
                  component="dt"
                  color="text.secondary"
                  variant="body2"
                >
                  Amount charged
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, fontWeight: 600 }}
                >
                  {formatMoney(selected.amount_charged, selected.currency)}
                </Typography>
                <Typography
                  component="dt"
                  color="text.secondary"
                  variant="body2"
                >
                  Refund
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, fontWeight: 600 }}
                >
                  No refund — the charge is kept (a no-show is not refunded).
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                autoFocus
                variant="outlined"
                size="small"
                onClick={backToDetailFromNoShow}
                disabled={markingNoShow}
              >
                Back
              </Button>
              <Button
                color="error"
                variant="contained"
                size="small"
                onClick={handleConfirmNoShow}
                disabled={markingNoShow}
              >
                {markingNoShow ? "Marking…" : "Confirm no-show"}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {canScheduleAnchor ? (
        <>
          <CreateBookingDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
            facilityId={facilityId}
            facilityName={facility.name}
            facilityTimeZone={facilityTimeZone}
            playerAllocation={facility?.per_court_allocation ?? null}
            courts={courts}
            returnFocusRef={createTriggerRef}
            canWaivePayment={hasStaffScheduleRole(facility?.allowed_actions)}
            onCreated={() => void loadBookingsFresh()}
          />
          <BlockCourtDialog
            open={blockOpen}
            onOpenChange={setBlockOpen}
            facilityId={facilityId}
            facilityName={facility.name}
            facilityTimeZone={facilityTimeZone}
            courts={courts}
            returnFocusRef={blockTriggerRef}
            onCreated={() => {
              void loadBookingsFresh();
            }}
          />
        </>
      ) : null}

      <Snackbar
        open={Boolean(
          feedback && (!selected || feedback.severity === "success"),
        )}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={(_event, reason) => {
          if (reason !== "clickaway") setFeedback(null);
        }}
      >
        {feedback ? (
          <Alert
            severity={feedback.severity}
            variant="filled"
            onClose={() => setFeedback(null)}
          >
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}
